import React from "react";
import { Snackbar, Alert, AlertTitle, Box } from "@mui/material";
import {
  SIGNUP_STATUS_LABELS,
  ATTENTION_STATUSES,
  severityForStatus,
} from "./signupStatusLabels";

const formatAmount = (amount) => {
  if (amount === null || amount === undefined) return null;
  return Number(amount).toLocaleString("pl-PL");
};

// Build a Polish, admin-facing detail line from the response meta. The
// backend response shape is the dict returned by
// `UserToLessonService.add_user`, e.g.:
//   {status, signup_status, signup_kind, pass_type_name,
//    pass_remaining_after, pass_just_exhausted, amount_owed,
//    waitlist_position}
const buildDetail = (result) => {
  if (!result) return "";
  const status = result.signup_status;
  const passName = result.pass_type_name;
  const remaining = result.pass_remaining_after;
  const amount = formatAmount(result.amount_owed);
  const position = result.waitlist_position;

  switch (status) {
    case "signed_with_pass":
      if (passName && remaining !== null && remaining !== undefined) {
        return `Karnet: ${passName} — pozostało ${remaining} ${
          remaining === 1 ? "lekcja" : "lekcji"
        }.`;
      }
      if (passName) return `Karnet: ${passName}.`;
      return "Zapis z karnetu.";
    case "signed_pass_exhausted":
      return passName
        ? `Karnet ${passName} został wyczerpany — to ostatnie wejście. Rozważ wystawienie nowego karnetu.`
        : "Karnet został wyczerpany — to ostatnie wejście. Rozważ wystawienie nowego karnetu.";
    case "signed_no_pass_unpaid":
      return amount
        ? `Brak aktywnego karnetu. Stworzono zapis z DŁUGIEM ${amount} PLN.`
        : "Brak aktywnego karnetu. Stworzono zapis z długiem.";
    case "signed_on_site_unpaid":
      return amount
        ? `Zajęcia płatne na miejscu (${amount} PLN).`
        : "Zajęcia płatne na miejscu.";
    case "signed_split_pending":
      return "Płatność podzielona — oczekująca.";
    case "signed_online_pending":
      return "Płatność online — oczekująca.";
    case "signed_free":
      return "Zajęcia bezpłatne.";
    case "waitlisted":
      // Use explicit null/undefined check rather than a truthy test, so a
      // theoretical position 0 (or any falsy-but-valid value) still renders.
      return position !== null && position !== undefined
        ? `Dodano do listy oczekujących (pozycja ${position}).`
        : "Dodano do listy oczekujących.";
    case "failed":
      return result.message || "Operacja nie powiodła się.";
    default:
      // Unknown / future status — show a sane fallback
      return result.message || "";
  }
};

/**
 * Snackbar that surfaces the result of an admin signup so the admin sees
 * at a glance whether the lesson came off a pass, exhausted it, or created
 * a debt.
 *
 * Props:
 *   open        — whether the snackbar is shown
 *   result      — full response body from POST /api/user_to_lesson/, or null
 *   userName    — display name of the signed-up user (optional, used in title)
 *   onClose     — called on dismiss
 */
const SignupResultSnackbar = ({ open, result, userName, onClose }) => {
  const status = result?.signup_status;
  const severity = severityForStatus(status);
  const meta = SIGNUP_STATUS_LABELS[status];
  const requiresAttention = ATTENTION_STATUSES.has(status);
  const autoHideDuration = requiresAttention ? null : 6000;

  const titleLabel = meta?.label || "Zapisano";
  const title = userName ? `${userName} — ${titleLabel}` : titleLabel;
  const detail = buildDetail(result);

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={(_event, reason) => {
        // Do not auto-close attention-required snackbars on clickaway.
        if (reason === "clickaway" && requiresAttention) return;
        onClose?.();
      }}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert
        severity={severity}
        variant="filled"
        onClose={onClose}
        sx={{ width: "100%", maxWidth: 560 }}
      >
        <AlertTitle sx={{ fontWeight: 600 }}>{title}</AlertTitle>
        {detail ? <Box>{detail}</Box> : null}
      </Alert>
    </Snackbar>
  );
};

export default SignupResultSnackbar;
